import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import i18n, { isRtl, type Locale } from '../i18n/config';

interface UiLanguageState {
  locale: Locale;
  direction: 'ltr' | 'rtl';
  setLocale: (locale: Locale) => void;
  toggle: () => void;
}

// BRD §11.3: AR-default for all MENA users. Initial state is hard-coded to
// 'ar' so a fresh visit (no persisted store, no localStorage `jylaw.lang`)
// lands on Arabic/RTL. Persist middleware rehydrates from localStorage on
// subsequent visits, so a user who toggled to EN stays in EN.
const DEFAULT_LOCALE: Locale = 'ar';

export const useUiLanguageStore = create<UiLanguageState>()(
  persist(
    (set, get) => ({
      locale: DEFAULT_LOCALE,
      direction: isRtl(DEFAULT_LOCALE) ? 'rtl' : 'ltr',
      setLocale: (locale) => {
        i18n.changeLanguage(locale);
        document.documentElement.lang = locale;
        document.documentElement.dir = isRtl(locale) ? 'rtl' : 'ltr';
        set({ locale, direction: isRtl(locale) ? 'rtl' : 'ltr' });
      },
      toggle: () => {
        const next: Locale = get().locale === 'ar' ? 'en' : 'ar';
        get().setLocale(next);
      },
    }),
    {
      name: 'jylaw.ui-language',
      // After Zustand rehydrates (or determines no persisted state exists),
      // sync i18next + <html lang/dir> to whatever the store ended up with.
      // Without this, fresh visit shows AR markup but i18next may still be
      // mid-init; on rehydrate-to-EN reload, we re-sync to EN.
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        if (i18n.language !== state.locale) {
          i18n.changeLanguage(state.locale);
        }
        if (typeof document !== 'undefined') {
          document.documentElement.lang = state.locale;
          document.documentElement.dir = state.direction;
        }
      },
    },
  ),
);

// Fresh-visit guarantee: if persist found no stored value, the initial state
// above is in effect — push it to <html> + i18next immediately so SSR/initial
// paint matches AR/RTL without waiting for RTLProvider's effect.
if (typeof document !== 'undefined') {
  const { locale, direction } = useUiLanguageStore.getState();
  document.documentElement.lang = locale;
  document.documentElement.dir = direction;
  if (i18n.language !== locale) {
    i18n.changeLanguage(locale);
  }
}
